import { current, isDraft, produce } from 'immer'
import { nanoid } from 'nanoid'
import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'

import { getStudioUser } from '@/api/client'
import { putAnnotation, type AnnotationBody, type AnnotationEnvelope, type ProposalResolution } from '@/api/annotations'
import type { DeepAnnotateMode, DeepFinding } from '@/api/jobs'
import { normalizeWinding, clampPt, rectRing, bandRing, round2 } from '@/geo/polygon'
import { simplifyForSave } from '@/geo/simplify'
import { DEFAULT_SNAP_FLAGS, type SnapFlags } from '@/geo/snap'
import type { Pt, Ring } from '@/geo/types'
import { convertInDoc, type ConvertOutcome } from '@/features/annotator/convert'
import type { ToolEffect, ToolId, ToolState } from '@/features/annotator/tools/types'
import type { AnnotationKind, Facing, Lint, Portal, Provenance, ReviewStatus, StageBody } from '@/schema'
import { useVocabStore } from '@/stores/vocabStore'

/**
 * The annotation editor's state (§9.1). One store per open asset; it is a
 * module singleton because only one asset is open at a time and because the
 * shortcut layer, the save controls and the canvas all need the same handle.
 *
 * Invariants worth knowing before changing anything here:
 *
 *  - **Nothing is written to the server except by an explicit save.** `save()` is called by the
 *    Save button, Ctrl+S, "Save and leave", "Save and go back" and the confirm in front of a
 *    publish / status change — never by a timer, a tab switch or an unmount.
 *  - **`dirty` is `doc !== lastSavedDoc`** (object identity, like the skeleton editor). An undo
 *    that lands back on the saved document is clean again; an edit made while a save is on its
 *    way stays dirty after the answer lands.
 *  - **`commit` is the one edit path** (`patchDoc` is its old name). Every edit makes exactly one
 *    undo step: a pointer gesture (`beginGesture` … `endGesture`) is one step, and consecutive
 *    edits that share a `coalesce` key within {@link COALESCE_MS} (typing in a field, dragging a
 *    slider, arrow-key nudges) merge into one. A new edit clears redo. Undo/redo do nothing while
 *    a gesture is open, and bring back the selection the document had.
 *  - **The server's lints never make an undo step.** They arrive for one document object (a
 *    save's answer, the dry-run lint, a merge preview) and are swapped in without touching
 *    history or `dirty` ({@link EditorState.applyServerLints}). They still live at `doc.lints`,
 *    which is where the canvas, the Check tab and the status pill read them.
 *  - **Accepting a suggestion is a local edit.** `applyProposalMerge` puts the server's dry-run
 *    merge in as one undoable step and remembers which proposal items it took; the next save
 *    reports them (`resolved_proposals`) in the same write.
 *  - **Lints never block a save** (§4.3.4). The server always writes a schema-valid body and
 *    answers with the derived lints; `saveError` is only ever a conflict, a client bug (422) or
 *    the network.
 */

export type EditorDoc = AnnotationBody

export interface EditorMeta {
  uid: string
  kind: AnnotationKind
  rev: number
  status: ReviewStatus
  vocab_rev: number
  published_rev: number | null
  published_tier: 'auto' | 'reviewed' | null
  assetKey: string
}

export interface EditorSelection {
  ids: string[]
  vertex?: { id: string; index: number }
}

export interface EditorHover {
  id?: string
  vertex?: number
  edge?: number
}

export interface Viewport {
  k: number
  tx: number
  ty: number
}

export type SaveError =
  | { kind: 'conflict'; current_rev: number }
  | { kind: 'schema'; message: string }
  | { kind: 'network'; message: string }
  | { kind: 'other'; message: string }

export type ProposalFinding = DeepFinding

export interface ProposalView {
  model?: string | null
  provider?: string | null
  id: number
  /** The picture it was made for. Opening another picture puts the review bar away. */
  assetUid?: string
  body: EditorDoc
  /** The server's diff from when the proposal was fetched. The review bar recomputes its own. */
  diff: { added: string[]; changed: { id: string; iou?: number; dist?: number }[]; unchanged: string[]; removed: string[]; locked_skipped: string[] }
  view: 'off' | 'overlay' | 'split' | 'swipe'
  threshold: number
  /** Who made it: Claude, the mock adapter, or the studio's own measuring. */
  source?: 'ai' | 'mock' | 'heuristic'
  /** How the run went: `ai`, `mock` (no AI connected), `heuristic_only` (Claude unavailable). */
  mode?: DeepAnnotateMode
  flags?: string[]
  /** What the run's checks found (open ones, and the ones it fixed itself). */
  findings?: ProposalFinding[]
  jobId?: number
  costUsd?: number
  /** A proposal feature id the review bar is pointing at (hovered row) — the ghost layer highlights it. */
  focus?: string | null
}

/** Proposal feature ids already taken into the document, per proposal id (as a string key). */
export type AcceptedMap = Readonly<Record<string, readonly string[]>>

/** One undo step: the document, the selection it had, and which suggestions it contained. */
export interface HistoryEntry {
  doc: EditorDoc
  selection: EditorSelection
  accepted: AcceptedMap
}

export interface EditorHistory {
  past: readonly HistoryEntry[]
  future: readonly HistoryEntry[]
}

export interface CommitOptions {
  /**
   * Consecutive edits with the same key, each within {@link COALESCE_MS} of the last, become one
   * undo step (`field:<id>:label`, `nudge:<ids>`, `simple:horizon`).
   */
  coalesce?: string
  /** Same as {@link coalesce} (the name the kind panels use). */
  coalesceKey?: string
}

/** The four stage vocab domains a feature's `kind` comes from. */
export type KindDomain = 'region_kind' | 'anchor_kind' | 'portal_kind' | 'path_kind'

export interface EditorState {
  doc: EditorDoc | null
  meta: EditorMeta | null
  dirty: boolean
  saving: boolean
  savedAt: string | null
  saveError: SaveError | null

  /** Undo / redo stacks. Read `selectCanUndo` / `useCanUndo` rather than the arrays. */
  history: EditorHistory
  /** A pointer gesture (drag, draw) is open: undo, redo and discard wait for it. */
  gesturing: boolean
  /** Suggestion items the document contains, per proposal — part of every undo step. */
  proposalAccepted: AcceptedMap
  /** Suggestion items the reviewer dropped, per proposal (not an undo step: nothing on the picture changes). */
  proposalDropped: Readonly<Record<string, readonly string[]>>

  tool: ToolId
  toolState: ToolState
  selection: EditorSelection
  hover: EditorHover
  viewport: Viewport
  proposal: ProposalView | null
  snapping: SnapFlags
  visibility: Record<string, boolean>
  solo: string | null
  /** Kind stamped on the next drawn feature, per domain (`1–9` sets it). */
  activeKind: { region?: string; anchor?: string; portal?: string; path?: string }

  /* --- lifecycle --- */
  load(env: AnnotationEnvelope): void
  reset(): void
  /** Take a newer server version as one undoable step (a "Use theirs", or a newer rev while clean). */
  adoptServerVersion(env: AnnotationEnvelope): void

  /* --- the one mutation path --- */
  commit(fn: (d: EditorDoc) => void, opts?: CommitOptions): boolean
  /** The old name of {@link commit}. */
  patchDoc(fn: (d: EditorDoc) => void, opts?: CommitOptions): void
  beginGesture(): void
  endGesture(): void
  cancelGesture(): void
  /** The next edit starts a new undo step even if it shares a coalesce key (a field lost focus). */
  breakCoalescing(): void
  undo(): boolean
  redo(): boolean
  /** Go back to the last saved document, as an undoable step. */
  discard(): boolean

  /* --- tool machine --- */
  setTool(tool: ToolId): void
  setToolState(state: ToolState): void
  applyEffects(effects: readonly ToolEffect[]): void

  /* --- selection, hover, viewport --- */
  select(ids: string[], additive?: boolean): void
  selectVertex(id: string, index: number): void
  clearSelection(): void
  setHover(hover: EditorHover): void
  setViewport(vp: Partial<Viewport>): void
  fit(): void
  zoomTo(k: number): void

  /* --- panels --- */
  setProposal(p: ProposalView | null): void
  setProposalView(view: ProposalView['view']): void
  setProposalThreshold(threshold: number): void
  setProposalFocus(id: string | null): void
  dropProposalItems(ids: readonly string[]): void
  undropProposalItems(ids: readonly string[]): void
  /** Put a server dry-run merge in as ONE undoable edit; `acceptedIds` are proposal feature ids. */
  applyProposalMerge(merged: EditorDoc, proposalId: number, acceptedIds: readonly string[]): boolean
  toggleSnap(key: keyof SnapFlags): void
  toggleVisible(id: string, visible?: boolean): void
  setSolo(id: string | null): void
  setActiveKind(domain: 'region' | 'anchor' | 'portal' | 'path', kind: string | undefined): void

  /* --- editing commands the UI calls directly --- */
  /** With a `domain`, only selected features of that domain change (a region kind never lands on a spot). */
  setKindOfSelection(kind: string, domain?: KindDomain | 'region' | 'anchor' | 'portal' | 'path'): void
  setKindOf(id: string, kind: string): void
  /**
   * Turn features into another list — a shape into a spot, a spot into a shape or a way in/out, a
   * way in/out into a spot — or, when already of `to`, into another kind of it. ONE undo step
   * (the selection moves to the results with it); ids stay where the schema allows and links stay
   * valid (see `features/annotator/convert.ts`). Nothing is saved.
   */
  convertFeatures(ids: readonly string[], to: KindDomain, kind: string): ConvertOutcome
  deleteSelection(): void
  duplicateSelection(): void
  nudgeSelection(dx: number, dy: number): void
  setFeatureField(id: string, field: string, value: unknown): void
  /** Per-kind field values live in `feature.props` (H4); `undefined` / `''` removes the key. */
  setFeatureProp(id: string, key: string, value: string | number | boolean | undefined): void
  setLocked(id: string, locked: boolean): void

  /* --- persistence --- */
  save(): Promise<AnnotationEnvelope | null>
  /** Re-send what is on screen on top of the newer revision (a 409's "Keep mine"). */
  keepMine(): Promise<AnnotationEnvelope | null>
  /** Take `rev`, `status` and publish state from an envelope; the document is left alone. */
  applyServerMeta(env: AnnotationEnvelope): void
  /** The old name of {@link applyServerMeta}. */
  applyEnvelope(env: AnnotationEnvelope): void
  /** Swap in server lints computed for `forDoc` — only if it is still the document. Never an undo step. */
  applyServerLints(forDoc: EditorDoc, lints: Lint[], affordances: string[]): boolean
  markSaveError(err: SaveError | null): void
}

/* -------------------------------------------------------------------------- */
/* Helpers                                                                     */
/* -------------------------------------------------------------------------- */

const nowIso = (): string => new Date().toISOString()

/** The provenance a human edit stamps on whatever it touched (§9.1). */
export function humanProvenance(): Provenance {
  return { source: 'human', confidence: 1, at: nowIso(), by: `user:${getStudioUser()}`, locked: true }
}

export function isStageBody(doc: EditorDoc | null | undefined): doc is StageBody {
  return !!doc && 'regions' in doc && 'depth' in doc
}

type StageFeature = StageBody['regions'][number] | StageBody['anchors'][number] | StageBody['portals'][number] | StageBody['paths'][number] | StageBody['layers'][number]

/** Every feature of a stage document in one list, for id lookups. */
export function stageFeatures(doc: StageBody): StageFeature[] {
  return [...doc.regions, ...doc.anchors, ...doc.portals, ...doc.paths, ...doc.layers]
}

export type StageKindedFeature = StageBody['regions'][number] | StageBody['anchors'][number] | StageBody['portals'][number] | StageBody['paths'][number]

/** Every feature that carries a vocabulary `kind` (i.e. everything but layers). */
export function stageKindedFeatures(doc: StageBody): StageKindedFeature[] {
  return [...doc.regions, ...doc.anchors, ...doc.portals, ...doc.paths]
}

/** The vocab domain of a feature id, or undefined for a layer / an unknown id. */
export function domainOfId(doc: EditorDoc | null, id: string): KindDomain | undefined {
  if (!isStageBody(doc)) return undefined
  if (doc.regions.some((r) => r.id === id)) return 'region_kind'
  if (doc.anchors.some((a) => a.id === id)) return 'anchor_kind'
  if (doc.portals.some((p) => p.id === id)) return 'portal_kind'
  if (doc.paths.some((p) => p.id === id)) return 'path_kind'
  return undefined
}

export function findRegion(doc: StageBody, id: string): StageBody['regions'][number] | undefined {
  return doc.regions.find((r) => r.id === id)
}

/** Outer ring of whatever feature `id` names (regions and layers have one; paths give their points). */
export function ringOf(doc: EditorDoc | null, id: string): Ring | undefined {
  if (!isStageBody(doc)) return undefined
  const region = doc.regions.find((r) => r.id === id)
  if (region) return region.poly.outer
  const path = doc.paths.find((p) => p.id === id)
  if (path) return path.points
  const anchor = doc.anchors.find((a) => a.id === id)
  if (anchor?.footprint) return anchor.footprint.outer
  return undefined
}

const newId = (prefix: string): string => `${prefix}_${nanoid(8)}`

/** Clip, round to 2 decimals and simplify everything on the way to the server (§4.0). */
export function normaliseForSave(doc: EditorDoc): EditorDoc {
  if (!isStageBody(doc)) return doc
  const body: StageBody = {
    ...doc,
    regions: doc.regions.map((r) => {
      const poly = normalizeWinding(simplifyForSave({ outer: r.poly.outer.map(clampPt), holes: r.poly.holes?.map((h) => h.map(clampPt)) }))
      return {
        ...r,
        poly: {
          outer: poly.outer.map(round2Pt),
          ...(poly.holes && poly.holes.length ? { holes: poly.holes.map((h) => h.map(round2Pt)) } : {}),
        },
      }
    }),
    anchors: doc.anchors.map((a) => ({
      ...a,
      at: round2Pt(clampPt(a.at)),
      ...(a.stand ? { stand: round2Pt(clampPt(a.stand)) } : {}),
    })),
    // Older AI merges left `at` / `label` on portals, which the schema refuses (422 on every save).
    portals: doc.portals.map((p) => {
      const legacy = p as Portal & { at?: unknown; label?: unknown }
      if (legacy.at === undefined && legacy.label === undefined) return p
      const { at: _at, label: _label, ...rest } = legacy
      return rest as Portal
    }),
    paths: doc.paths.map((p) => ({ ...p, points: p.points.map((q) => round2Pt(clampPt(q))) })),
  }
  return body
}

const round2Pt = (p: Pt): Pt => [round2(p[0]), round2(p[1])]

/** A deep, plain, mutable copy of a (possibly immer-draft) feature — `structuredClone` refuses drafts. */
function plainCopy<T extends object>(value: T): T {
  return structuredClone(isDraft(value) ? current(value) : value)
}

const SLOT_DOMAIN: Record<string, KindDomain> = { region: 'region_kind', anchor: 'anchor_kind', portal: 'portal_kind', path: 'path_kind' }

function normaliseDomain(domain: string | undefined): KindDomain | undefined {
  if (!domain) return undefined
  return SLOT_DOMAIN[domain] ?? (domain as KindDomain)
}

/** Keep only selected ids that exist in `doc`. */
function pruneFor(doc: EditorDoc | null, selection: EditorSelection): EditorSelection {
  if (!isStageBody(doc)) return { ids: [] }
  const live = new Set(stageFeatures(doc).map((f) => f.id))
  const ids = selection.ids.filter((id) => live.has(id))
  const vertex = selection.vertex && live.has(selection.vertex.id) ? selection.vertex : undefined
  return vertex ? { ids, vertex } : { ids }
}

/* -------------------------------------------------------------------------- */
/* Store                                                                       */
/* -------------------------------------------------------------------------- */

export const HISTORY_LIMIT = 200
/** Same-key edits closer together than this merge into one undo step. */
export const COALESCE_MS = 1000

const EMPTY_HISTORY: EditorHistory = { past: [], future: [] }

const INITIAL = {
  doc: null as EditorDoc | null,
  meta: null as EditorMeta | null,
  dirty: false,
  saving: false,
  savedAt: null as string | null,
  saveError: null as SaveError | null,
  history: EMPTY_HISTORY,
  gesturing: false,
  proposalAccepted: {} as AcceptedMap,
  proposalDropped: {} as EditorState['proposalDropped'],
  tool: 'select' as ToolId,
  toolState: { t: 'idle' } as ToolState,
  selection: { ids: [] } as EditorSelection,
  hover: {} as EditorHover,
  viewport: { k: 1, tx: 0, ty: 0 } as Viewport,
  proposal: null as ProposalView | null,
  snapping: { ...DEFAULT_SNAP_FLAGS } as SnapFlags,
  visibility: {} as Record<string, boolean>,
  solo: null as string | null,
  activeKind: {} as EditorState['activeKind'],
}

/* Module state that must not re-render anything. Reset by `load` / `reset`. */

/** The document the server holds (identity). `dirty` = `doc !== lastSavedDoc`. */
let lastSavedDoc: EditorDoc | null = null
/** `proposalAccepted` as of `lastSavedDoc` — what "Discard" goes back to. */
let lastSavedAccepted: AcceptedMap = {}
/** State when the open gesture began; `endGesture` pushes it as the one undo step. */
let gestureStart: HistoryEntry | null = null
let gestureDepth = 0
/** The last coalescable edit: merge into it while the key matches, it is recent and nothing else happened. */
let lastCoalesce: { key: string; at: number; pastLen: number } | null = null
/** Documents whose `lints` / `affordances` came from the server — no need to lint them again. */
let serverLinted = new WeakSet<object>()
/** The document a save in flight sent (re-pointed if its lints are swapped in meanwhile). */
let inflight: { sent: EditorDoc } | null = null
/** Last resolution sent per proposal id, so an unrelated later save does not re-send it. */
let sentResolutions = new Map<string, string>()

function resetModuleState(saved: EditorDoc | null): void {
  lastSavedDoc = saved
  lastSavedAccepted = {}
  gestureStart = null
  gestureDepth = 0
  lastCoalesce = null
  serverLinted = new WeakSet<object>()
  if (saved) serverLinted.add(saved)
  inflight = null
  sentResolutions = new Map()
}

const pushBounded = (xs: readonly HistoryEntry[], entry: HistoryEntry): HistoryEntry[] => [...xs, entry].slice(-HISTORY_LIMIT)

/**
 * Undo steps that hold `from` hold `to` instead — the same document with the server's lints
 * swapped in. Without it an undo back to the saved state would land on the pre-lint object and
 * read as "Unsaved changes" (`dirty` is by identity).
 */
function repointHistory(h: EditorHistory, from: EditorDoc, to: EditorDoc): EditorHistory {
  const fix = (xs: readonly HistoryEntry[]): readonly HistoryEntry[] => (xs.some((e) => e.doc === from) ? xs.map((e) => (e.doc === from ? { ...e, doc: to } : e)) : xs)
  const past = fix(h.past)
  const future = fix(h.future)
  return past === h.past && future === h.future ? h : { past, future }
}

function metaOf(env: AnnotationEnvelope): EditorMeta {
  return {
    uid: env.asset.uid,
    kind: kindOfSchema(env.schema),
    rev: env.rev,
    status: env.status,
    vocab_rev: env.vocab_rev,
    published_rev: env.published_rev,
    published_tier: env.published_tier,
    assetKey: env.asset.key,
  }
}

function withServerLints(doc: StageBody, lints: readonly Lint[], affordances: readonly string[]): StageBody {
  const next = produce(doc, (d) => {
    d.lints = lints as Lint[]
    d.affordances = affordances as string[]
  })
  serverLinted.add(next)
  return next
}

export const useEditorStore = create<EditorState>()(
  immer<EditorState>((set, get) => {
    /**
     * Make `next` the document. Outside a gesture this is one undo step (or merges into the
     * previous one, see {@link CommitOptions.coalesce}); inside one the gesture's end records it.
     */
    const commitNext = (next: EditorDoc, opts?: CommitOptions, extra?: Partial<EditorState>): boolean => {
      const s = get()
      if (!s.doc || next === s.doc) {
        if (extra) set(extra)
        return false
      }
      if (gestureDepth > 0) {
        set({ ...extra, doc: next, dirty: next !== lastSavedDoc })
        return true
      }
      const at = Date.now()
      const { past, future } = s.history
      const key = opts?.coalesce ?? opts?.coalesceKey
      const merge =
        key !== undefined &&
        lastCoalesce !== null &&
        lastCoalesce.key === key &&
        at - lastCoalesce.at <= COALESCE_MS &&
        past.length === lastCoalesce.pastLen &&
        future.length === 0
      const nextPast = merge ? past : pushBounded(past, { doc: s.doc, selection: s.selection, accepted: s.proposalAccepted })
      lastCoalesce = key !== undefined ? { key, at, pastLen: nextPast.length } : null
      set({ ...extra, doc: next, dirty: next !== lastSavedDoc, history: { past: nextPast, future: [] } })
      return true
    }

    const commit = (fn: (d: EditorDoc) => void, opts?: CommitOptions, extra?: Partial<EditorState>): boolean => {
      const doc = get().doc
      if (!doc) return false
      const next = produce(doc, (d) => {
        fn(d as EditorDoc)
      })
      return commitNext(next, opts, extra)
    }

    const snapshot = (): HistoryEntry | null => {
      const s = get()
      return s.doc ? { doc: s.doc, selection: s.selection, accepted: s.proposalAccepted } : null
    }

    return {
      ...INITIAL,

      /* ---------------------------------------------------------------- */
      load(env) {
        resetModuleState(env.body)
        // Suggestions belong to one picture: moving to another one (the route stays mounted for
        // back/forward and "next") must not leave the last picture's review bar up — accepting
        // from it would merge the wrong proposal and every later save would be refused (422).
        const s = get()
        const p = s.proposal
        const foreign = p !== null && (p.assetUid !== undefined ? p.assetUid !== env.asset.uid : s.meta !== null && s.meta.uid !== env.asset.uid)
        set({
          ...(foreign ? { proposal: null } : {}),
          doc: env.body,
          meta: metaOf(env),
          dirty: false,
          saveError: null,
          selection: { ids: [] },
          visibility: {},
          solo: null,
          toolState: { t: 'idle' },
          history: EMPTY_HISTORY,
          gesturing: false,
          proposalAccepted: {},
          proposalDropped: {},
        })
      },

      reset() {
        resetModuleState(null)
        set(() => ({ ...INITIAL, snapping: { ...DEFAULT_SNAP_FLAGS } }) as EditorState)
      },

      adoptServerVersion(env) {
        const s = get()
        if (!s.doc || !s.meta || s.meta.uid !== env.asset.uid) {
          get().load(env)
          return
        }
        // A gesture cannot survive the document changing under it.
        gestureDepth = 0
        gestureStart = null
        lastSavedDoc = env.body
        lastSavedAccepted = {}
        serverLinted.add(env.body)
        sentResolutions = new Map()
        commitNext(env.body, undefined, {
          meta: metaOf(env),
          saveError: null,
          gesturing: false,
          proposalAccepted: {},
          selection: pruneFor(env.body, s.selection),
          toolState: { t: 'idle' },
        })
        set({ dirty: get().doc !== lastSavedDoc })
      },

      /* ---------------------------------------------------------------- */
      commit(fn, opts) {
        return commit(fn, opts)
      },

      patchDoc(fn, opts) {
        commit(fn, opts)
      },

      beginGesture() {
        gestureDepth += 1
        if (gestureDepth > 1) return
        gestureStart = snapshot()
        set({ gesturing: true })
      },

      endGesture() {
        if (gestureDepth === 0) return
        gestureDepth -= 1
        if (gestureDepth > 0) return
        const start = gestureStart
        gestureStart = null
        lastCoalesce = null
        const s = get()
        if (!start || !s.doc || start.doc === s.doc) {
          set({ gesturing: false })
          return
        }
        set({ gesturing: false, dirty: s.doc !== lastSavedDoc, history: { past: pushBounded(s.history.past, start), future: [] } })
      },

      cancelGesture() {
        if (gestureDepth > 1) {
          gestureDepth -= 1
          return
        }
        const start = gestureStart
        gestureDepth = 0
        gestureStart = null
        // The rollback is not an undo step: Esc during a drag leaves no trace at all.
        const restored = start ? start.doc : get().doc
        set({
          gesturing: false,
          toolState: { t: 'idle' },
          ...(start ? { doc: start.doc, dirty: start.doc !== lastSavedDoc, selection: pruneFor(restored, get().selection) } : {}),
        })
      },

      breakCoalescing() {
        lastCoalesce = null
      },

      undo() {
        if (gestureDepth > 0) return false
        const s = get()
        const past = s.history.past
        const entry = past[past.length - 1]
        if (!entry || !s.doc) return false
        lastCoalesce = null
        set({
          doc: entry.doc,
          selection: pruneFor(entry.doc, entry.selection),
          proposalAccepted: entry.accepted,
          dirty: entry.doc !== lastSavedDoc,
          history: { past: past.slice(0, -1), future: pushBounded(s.history.future, { doc: s.doc, selection: s.selection, accepted: s.proposalAccepted }) },
        })
        return true
      },

      redo() {
        if (gestureDepth > 0) return false
        const s = get()
        const future = s.history.future
        const entry = future[future.length - 1]
        if (!entry || !s.doc) return false
        lastCoalesce = null
        set({
          doc: entry.doc,
          selection: pruneFor(entry.doc, entry.selection),
          proposalAccepted: entry.accepted,
          dirty: entry.doc !== lastSavedDoc,
          history: { past: pushBounded(s.history.past, { doc: s.doc, selection: s.selection, accepted: s.proposalAccepted }), future: future.slice(0, -1) },
        })
        return true
      },

      discard() {
        const s = get()
        if (!s.doc || !lastSavedDoc || s.doc === lastSavedDoc || gestureDepth > 0) return false
        return commitNext(lastSavedDoc, undefined, {
          proposalAccepted: lastSavedAccepted,
          selection: pruneFor(lastSavedDoc, s.selection),
          saveError: s.saveError?.kind === 'conflict' ? s.saveError : null,
        })
      },

      /* ---------------------------------------------------------------- */
      setTool(tool) {
        set((s) => {
          s.tool = tool
          s.toolState = { t: 'idle' }
        })
      },

      setToolState(state) {
        set((s) => {
          s.toolState = state
        })
      },

      applyEffects(effects) {
        for (const e of effects) applyEffect(get(), e)
      },

      /* ---------------------------------------------------------------- */
      select(ids, additive) {
        set((s) => {
          s.selection = { ids: additive ? [...new Set([...s.selection.ids, ...ids])] : [...ids] }
        })
      },

      selectVertex(id, index) {
        set((s) => {
          s.selection = { ids: [id], vertex: { id, index } }
        })
      },

      clearSelection() {
        set((s) => {
          s.selection = { ids: [] }
        })
      },

      setHover(hover) {
        set((s) => {
          s.hover = hover
        })
      },

      setViewport(vp) {
        set((s) => {
          s.viewport = { ...s.viewport, ...vp }
        })
      },

      fit() {
        set((s) => {
          s.viewport = { k: 1, tx: 0, ty: 0 }
        })
      },

      zoomTo(k) {
        set((s) => {
          s.viewport = { ...s.viewport, k: Math.min(16, Math.max(0.1, k)) }
        })
      },

      /* ---------------------------------------------------------------- */
      setProposal(p) {
        set({ proposal: p })
      },

      setProposalView(view) {
        set((s) => {
          if (s.proposal) s.proposal.view = view
        })
      },

      setProposalThreshold(threshold) {
        set((s) => {
          if (s.proposal) s.proposal.threshold = threshold
        })
      },

      setProposalFocus(id) {
        set((s) => {
          if (s.proposal && s.proposal.focus !== id) s.proposal.focus = id
        })
      },

      dropProposalItems(ids) {
        const p = get().proposal
        if (!p || ids.length === 0) return
        const key = String(p.id)
        const current = get().proposalDropped[key] ?? []
        set({ proposalDropped: { ...get().proposalDropped, [key]: [...new Set([...current, ...ids])] } })
      },

      undropProposalItems(ids) {
        const p = get().proposal
        if (!p || ids.length === 0) return
        const key = String(p.id)
        const gone = new Set(ids)
        const current = get().proposalDropped[key] ?? []
        set({ proposalDropped: { ...get().proposalDropped, [key]: current.filter((x) => !gone.has(x)) } })
      },

      applyProposalMerge(merged, proposalId, acceptedIds) {
        const s = get()
        if (!s.doc) return false
        const key = String(proposalId)
        const prev = s.proposalAccepted[key] ?? []
        const accepted: AcceptedMap = { ...s.proposalAccepted, [key]: [...new Set([...prev, ...acceptedIds])] }
        const taken = new Set(acceptedIds)
        const dropped = s.proposalDropped[key]
        serverLinted.add(merged)
        return commitNext(merged, undefined, {
          proposalAccepted: accepted,
          ...(dropped?.some((id) => taken.has(id)) ? { proposalDropped: { ...s.proposalDropped, [key]: dropped.filter((id) => !taken.has(id)) } } : {}),
          selection: pruneFor(merged, s.selection),
        })
      },

      toggleSnap(key) {
        set((s) => {
          if (key === 'grid') return
          s.snapping[key] = !s.snapping[key]
        })
      },

      toggleVisible(id, visible) {
        set((s) => {
          s.visibility[id] = visible ?? !(s.visibility[id] ?? true)
        })
      },

      setSolo(id) {
        set((s) => {
          s.solo = s.solo === id ? null : id
        })
      },

      setActiveKind(domain, kind) {
        set((s) => {
          s.activeKind = { ...s.activeKind, [domain]: kind }
        })
      },

      /* ---------------------------------------------------------------- */
      setKindOfSelection(kind, domain) {
        const { selection } = get()
        if (selection.ids.length === 0) return
        const only = normaliseDomain(domain)
        const ids = new Set(selection.ids)
        commit((d) => {
          if (!isStageBody(d)) return
          const retag = (xs: StageKindedFeature[], dom: KindDomain) => {
            if (only && only !== dom) return
            for (const f of xs) {
              if (!ids.has(f.id) || f.kind === kind) continue
              f.kind = kind
              f.provenance = humanProvenance()
            }
          }
          retag(d.regions, 'region_kind')
          retag(d.anchors, 'anchor_kind')
          retag(d.portals, 'portal_kind')
          retag(d.paths, 'path_kind')
        })
      },

      setKindOf(id, kind) {
        commit((d) => {
          if (!isStageBody(d)) return
          const f = stageKindedFeatures(d).find((x) => x.id === id)
          if (!f || f.kind === kind) return
          f.kind = kind
          f.provenance = humanProvenance()
        })
      },

      convertFeatures(ids, to, kind) {
        const s = get()
        const empty: ConvertOutcome = { selected: [], converted: [], retagged: [], removed: [], skipped: [], rehomed: [] }
        if (!isStageBody(s.doc) || gestureDepth > 0 || ids.length === 0) return empty
        let outcome: ConvertOutcome = empty
        const next = produce(s.doc, (d) => {
          outcome = convertInDoc(d as StageBody, ids, to, kind, { provenance: humanProvenance, newId })
        })
        if (next === s.doc) {
          // Already what was asked (a way in/out of that kind on that spot): just point at it.
          if (outcome.selected.length) set({ selection: { ids: outcome.selected } })
          return outcome
        }
        const gone = new Set(outcome.removed)
        commitNext(next, undefined, {
          selection: { ids: outcome.selected },
          ...(s.solo && gone.has(s.solo) ? { solo: null } : {}),
        })
        return outcome
      },

      deleteSelection() {
        const { selection } = get()
        if (selection.ids.length === 0) return
        const ids = new Set(selection.ids)
        commit(
          (d) => {
            if (!isStageBody(d)) return
            const gone: string[] = []
            const keep = <T extends { id: string }>(xs: T[]): T[] =>
              xs.filter((x) => {
                if (!ids.has(x.id)) return true
                gone.push(x.id)
                return false
              })
            d.regions = keep(d.regions)
            d.anchors = keep(d.anchors)
            d.portals = keep(d.portals)
            d.paths = keep(d.paths)
            d.layers = keep(d.layers)
            // A human deletion is permanent: a re-run may not resurrect it (§9.1).
            for (const id of gone) if (!d.tombstones.includes(id)) d.tombstones.push(id)
          },
          undefined,
          { selection: { ids: [] } },
        )
      },

      duplicateSelection() {
        const { selection } = get()
        if (selection.ids.length === 0) return
        const created: string[] = []
        const shift = (p: Pt): Pt => clampPt([p[0] + 2, p[1] + 2])
        const changed = commit((d) => {
          if (!isStageBody(d)) return
          for (const id of selection.ids) {
            const r = d.regions.find((x) => x.id === id)
            if (r) {
              const copy = plainCopy(r)
              copy.id = newId('r')
              copy.poly = { outer: r.poly.outer.map(shift), ...(r.poly.holes ? { holes: r.poly.holes.map((h) => h.map(shift)) } : {}) }
              copy.provenance = humanProvenance()
              d.regions.push(copy)
              created.push(copy.id)
              continue
            }
            const a = d.anchors.find((x) => x.id === id)
            if (a) {
              const copy = plainCopy(a)
              copy.id = newId('a')
              copy.at = shift(a.at)
              if (a.stand) copy.stand = shift(a.stand)
              if (a.footprint) copy.footprint = { outer: a.footprint.outer.map(shift), ...(a.footprint.holes ? { holes: a.footprint.holes.map((h) => h.map(shift)) } : {}) }
              if (a.slots) copy.slots = a.slots.map((sl) => ({ ...sl, at: shift(sl.at), ...(sl.stand ? { stand: shift(sl.stand) } : {}) }))
              copy.provenance = humanProvenance()
              d.anchors.push(copy)
              created.push(copy.id)
              continue
            }
            const w = d.paths.find((x) => x.id === id)
            if (w) {
              const copy = plainCopy(w)
              copy.id = newId('w')
              copy.points = w.points.map(shift)
              copy.provenance = humanProvenance()
              d.paths.push(copy)
              created.push(copy.id)
            }
            // Ways in/out are not copied: two doors on the same edge or spot mean nothing new.
          }
        })
        if (changed && created.length) get().select(created)
      },

      nudgeSelection(dx, dy) {
        const { selection } = get()
        if (selection.ids.length === 0) return
        const ids = [...selection.ids]
        commit((d) => translateIn(d, ids, [dx, dy]), { coalesce: `nudge:${[...ids].sort().join(',')}` })
      },

      setFeatureField(id, field, value) {
        commit(
          (d) => {
            if (!isStageBody(d)) return
            const feature = stageFeatures(d).find((f) => f.id === id) as Record<string, unknown> | undefined
            if (!feature) return
            const empty = value === undefined || value === ''
            if (empty ? !(field in feature) : feature[field] === value) return
            if (empty) delete feature[field]
            else feature[field] = value
            feature['provenance'] = humanProvenance()
          },
          { coalesce: `field:${id}:${field}` },
        )
      },

      setFeatureProp(id, key, value) {
        commit(
          (d) => {
            if (!isStageBody(d)) return
            const feature = stageKindedFeatures(d).find((f) => f.id === id)
            if (!feature) return
            const props = { ...(feature.props ?? {}) }
            const empty = value === undefined || value === ''
            if (empty ? !(key in props) : props[key] === value) return
            if (empty) delete props[key]
            else props[key] = value
            if (Object.keys(props).length) feature.props = props
            else delete feature.props
            feature.provenance = humanProvenance()
          },
          { coalesce: `prop:${id}:${key}` },
        )
      },

      setLocked(id, locked) {
        commit((d) => {
          if (!isStageBody(d)) return
          const feature = stageFeatures(d).find((f) => f.id === id)
          if (feature && (feature.provenance.locked === true) !== locked) feature.provenance = { ...feature.provenance, locked }
        })
      },

      /* ---------------------------------------------------------------- */
      async save() {
        const s = get()
        if (!s.doc || !s.meta || !s.dirty || s.saving) return null
        const sent = s.doc
        const uid = s.meta.uid
        const acceptedAtSend = s.proposalAccepted
        const resolutions = pendingResolutions(s)
        inflight = { sent }
        lastCoalesce = null
        set({ saving: true, saveError: null })
        try {
          const env = await putAnnotation(uid, {
            base_rev: s.meta.rev,
            body: normaliseForSave(sent),
            ...(resolutions.length ? { resolved_proposals: resolutions.map((r) => r.resolution) } : {}),
          })
          const saved = inflight?.sent ?? sent
          inflight = null
          const cur = get()
          if (!cur.meta || cur.meta.uid !== uid) return env // the editor was closed meanwhile
          for (const r of resolutions) sentResolutions.set(r.key, r.sig)
          lastSavedDoc = saved
          lastSavedAccepted = acceptedAtSend
          let doc = cur.doc
          let history = cur.history
          // Nothing changed while the request was out: the saved document takes the server's lints.
          if (doc === saved && isStageBody(doc) && isStageBody(env.body)) {
            doc = withServerLints(doc, env.body.lints, env.body.affordances)
            lastSavedDoc = doc
            history = repointHistory(history, saved, doc)
          }
          set({
            doc,
            history,
            dirty: doc !== lastSavedDoc,
            saving: false,
            savedAt: nowIso(),
            saveError: null,
            meta: { ...cur.meta, ...metaOf(env), kind: cur.meta.kind },
          })
          return env
        } catch (err) {
          inflight = null
          set({ saving: false, saveError: toSaveError(err) })
          return null
        }
      },

      async keepMine() {
        const s = get()
        const err = s.saveError
        if (!s.meta || err?.kind !== 'conflict') return null
        // Take the newer rev as the base and re-send exactly what is on screen. (A 409 left
        // `lastSavedDoc` alone, so the document is still dirty and `save` sends it.)
        set({ meta: { ...s.meta, rev: err.current_rev }, saveError: null })
        return get().save()
      },

      applyServerMeta(env) {
        set((s) => {
          if (!s.meta || s.meta.uid !== env.asset.uid) return
          s.meta.rev = Math.max(s.meta.rev, env.rev)
          s.meta.status = env.status
          s.meta.published_rev = env.published_rev
          s.meta.published_tier = env.published_tier
          s.meta.vocab_rev = env.vocab_rev
        })
      },

      applyEnvelope(env) {
        get().applyServerMeta(env)
      },

      applyServerLints(forDoc, lints, affordances) {
        const s = get()
        if (!s.doc || s.doc !== forDoc || gestureDepth > 0 || !isStageBody(forDoc)) return false
        const next = withServerLints(forDoc, lints, affordances)
        if (lastSavedDoc === forDoc) lastSavedDoc = next
        if (inflight?.sent === forDoc) inflight.sent = next
        set({ doc: next, dirty: next !== lastSavedDoc, history: repointHistory(s.history, forDoc, next) })
        return true
      },

      markSaveError(err) {
        set({ saveError: err, saving: false })
      },
    }
  }),
)

function kindOfSchema(schema: string): AnnotationKind {
  const m = /^story-studio\.(stage|character|object)\/1$/.exec(schema)
  return (m?.[1] as AnnotationKind) ?? 'stage'
}

function toSaveError(err: unknown): SaveError {
  const e = err as { status?: number; code?: string; message?: string; details?: Record<string, unknown> }
  if (e?.status === 409) return { kind: 'conflict', current_rev: Number(e.details?.['current_rev'] ?? 0) }
  if (e?.status === 422) return { kind: 'schema', message: e.message ?? 'The studio sent a document the server could not read.' }
  if (e?.status === 0 || e?.code === 'network') return { kind: 'network', message: 'Could not reach the studio server. Your work is still here.' }
  return { kind: 'other', message: e?.message ?? 'Saving failed.' }
}

/** Proposals this save resolves: those with items in the document, not already sent as they are. */
function pendingResolutions(s: EditorState): { key: string; sig: string; resolution: ProposalResolution }[] {
  const out: { key: string; sig: string; resolution: ProposalResolution }[] = []
  for (const [key, ids] of Object.entries(s.proposalAccepted)) {
    // A proposal nothing was taken from is left pending: dropping a few items is not a rejection.
    if (!ids.length) continue
    const id = Number(key)
    if (!Number.isInteger(id) || id < 1) continue
    const dropped = s.proposalDropped[key] ?? []
    const resolution: ProposalResolution = {
      id,
      accepted_ids: [...ids].sort(),
      ...(dropped.length ? { rejected_ids: [...dropped].sort() } : {}),
    }
    const sig = JSON.stringify(resolution)
    if (sentResolutions.get(key) === sig) continue
    out.push({ key, sig, resolution })
  }
  return out.slice(0, 20)
}

/* -------------------------------------------------------------------------- */
/* Effects from the tool machine                                               */
/* -------------------------------------------------------------------------- */

function translateIn(d: EditorDoc, ids: readonly string[], delta: Pt): void {
  if (!isStageBody(d)) return
  const set = new Set(ids)
  for (const r of d.regions) {
    if (!set.has(r.id)) continue
    r.poly.outer = r.poly.outer.map((p) => clampPt([p[0] + delta[0], p[1] + delta[1]]))
    if (r.poly.holes) r.poly.holes = r.poly.holes.map((h) => h.map((p) => clampPt([p[0] + delta[0], p[1] + delta[1]])))
    r.provenance = humanProvenance()
  }
  for (const a of d.anchors) {
    if (!set.has(a.id)) continue
    a.at = clampPt([a.at[0] + delta[0], a.at[1] + delta[1]])
    if (a.stand) a.stand = clampPt([a.stand[0] + delta[0], a.stand[1] + delta[1]])
    // The outline goes with the spot (a shape turned into a spot keeps its outline here).
    if (a.footprint) a.footprint = moveOutline(a.footprint, (p) => clampPt([p[0] + delta[0], p[1] + delta[1]]))
    a.provenance = humanProvenance()
  }
  for (const p of d.paths) {
    if (!set.has(p.id)) continue
    p.points = p.points.map((q) => clampPt([q[0] + delta[0], q[1] + delta[1]]))
    p.provenance = humanProvenance()
  }
}

/** A polygon with every point moved by `move` (a fresh object, never the one passed in). */
function moveOutline(poly: { outer: Pt[]; holes?: Pt[][] }, move: (p: Pt) => Pt): { outer: Pt[]; holes?: Pt[][] } {
  return { outer: poly.outer.map(move), ...(poly.holes ? { holes: poly.holes.map((h) => h.map(move)) } : {}) }
}

/**
 * A drag: place the features at their pre-drag geometry (`base`) plus the TOTAL offset. The offset
 * is clamped so the whole group stays on the picture — moving past an edge stops the group there
 * instead of squashing the shape against it.
 */
function translateFrom(d: EditorDoc, base: EditorDoc, ids: readonly string[], delta: Pt): void {
  if (!isStageBody(d) || !isStageBody(base)) return
  const set = new Set(ids)
  const pts: Pt[] = []
  for (const r of base.regions) if (set.has(r.id)) pts.push(...r.poly.outer)
  for (const a of base.anchors) if (set.has(a.id)) pts.push(a.at, ...(a.stand ? [a.stand] : []))
  for (const p of base.paths) if (set.has(p.id)) pts.push(...p.points)
  if (pts.length === 0) return
  const xs = pts.map((p) => p[0])
  const ys = pts.map((p) => p[1])
  const clamp = (v: number, lo: number, hi: number) => Math.min(Math.max(v, Math.min(lo, 0)), Math.max(hi, 0))
  const dx = clamp(delta[0], -Math.min(...xs), 100 - Math.max(...xs))
  const dy = clamp(delta[1], -Math.min(...ys), 100 - Math.max(...ys))
  const move = (p: Pt): Pt => clampPt([p[0] + dx, p[1] + dy])
  for (const r of d.regions) {
    const b = set.has(r.id) ? base.regions.find((x) => x.id === r.id) : undefined
    if (!b) continue
    r.poly = { outer: b.poly.outer.map(move), ...(b.poly.holes ? { holes: b.poly.holes.map((h) => h.map(move)) } : {}) }
    r.provenance = humanProvenance()
  }
  for (const a of d.anchors) {
    const b = set.has(a.id) ? base.anchors.find((x) => x.id === a.id) : undefined
    if (!b) continue
    a.at = move(b.at)
    if (b.stand) a.stand = move(b.stand)
    if (b.footprint) a.footprint = moveOutline(b.footprint, move)
    a.provenance = humanProvenance()
  }
  for (const p of d.paths) {
    const b = set.has(p.id) ? base.paths.find((x) => x.id === p.id) : undefined
    if (!b) continue
    p.points = b.points.map(move)
    p.provenance = humanProvenance()
  }
}

/**
 * The kind for a new way in/out when the tool does not name one. On an edge: the armed kind (the
 * kind bar arms one up front), else the first one that needs no spot. On a spot: the armed kind
 * only when it is one that stands on a spot (`needs_anchor`) — the kind bar arms "edge of the
 * picture" by default, which on a door would mean "walk off the side" — else a door-like kind from
 * the live list, so custom kinds work too.
 */
function portalKindFor(where: 'spot' | 'edge', armed: string | undefined): string {
  const idx = useVocabStore.getState().domains.portal_kind
  const rows = idx ? idx.order.map((id) => idx.byId[id]).filter((k): k is NonNullable<typeof k> => !!k && k.enabled && !k.deprecated) : []
  const onSpot = (k: (typeof rows)[number]) => (k.semantics as { needs_anchor?: unknown } | undefined)?.needs_anchor === true
  if (rows.length === 0) return where === 'spot' ? (armed && armed !== 'edge' ? armed : 'door') : (armed ?? 'edge')
  const armedRow = armed ? rows.find((k) => k.id === armed) : undefined
  if (where === 'edge') return armedRow?.id ?? rows.find((k) => !onSpot(k))?.id ?? armed ?? 'edge'
  if (armedRow && onSpot(armedRow)) return armedRow.id
  return rows.find((k) => k.id === 'door' && onSpot(k))?.id ?? rows.find(onSpot)?.id ?? armed ?? 'door'
}

/** Effects carried to the UI (toasts); the canvas drains this between renders. */
export type EditorToast = { message: string; tone: 'info' | 'success' | 'warning' | 'error' }

let toastSink: ((t: EditorToast) => void) | null = null

/** `AnnotatorLayout` wires the real toaster in on mount. */
export function setEditorToastSink(fn: ((t: EditorToast) => void) | null): void {
  toastSink = fn
}

function applyEffect(state: EditorState, e: ToolEffect): void {
  switch (e.k) {
    case 'beginGesture':
      state.beginGesture()
      return
    case 'endGesture':
      state.endGesture()
      return
    case 'cancelGesture':
      state.cancelGesture()
      return
    case 'select':
      state.select(e.ids, e.additive)
      return
    case 'selectVertex':
      state.selectVertex(e.id, e.index)
      return
    case 'clearSelection':
      state.clearSelection()
      return
    case 'selectInRect': {
      const doc = state.doc
      if (!isStageBody(doc)) return
      const x0 = Math.min(e.from[0], e.to[0])
      const x1 = Math.max(e.from[0], e.to[0])
      const y0 = Math.min(e.from[1], e.to[1])
      const y1 = Math.max(e.from[1], e.to[1])
      const inside = (p: Pt): boolean => p[0] >= x0 && p[0] <= x1 && p[1] >= y0 && p[1] <= y1
      const ids: string[] = []
      for (const r of doc.regions) if (r.poly.outer.some(inside)) ids.push(r.id)
      for (const a of doc.anchors) if (inside(a.at)) ids.push(a.id)
      for (const p of doc.paths) if (p.points.some(inside)) ids.push(p.id)
      state.select(ids, e.additive)
      return
    }
    case 'createRegion': {
      const kind = e.kind ?? state.activeKind.region ?? 'ground'
      const id = newId('r')
      state.patchDoc((d) => {
        if (!isStageBody(d)) return
        d.regions.push({ id, kind, poly: normalizeWinding({ outer: e.ring.map(clampPt) }), provenance: humanProvenance() })
      })
      state.select([id])
      return
    }
    case 'createBand': {
      const kind = e.kind ?? state.activeKind.region ?? 'ground'
      const id = newId('r')
      state.patchDoc((d) => {
        if (!isStageBody(d)) return
        d.regions.push({ id, kind, poly: { outer: bandRing(e.y0, e.y1) }, provenance: humanProvenance() })
      })
      state.select([id])
      return
    }
    case 'moveVertex':
      state.patchDoc((d) => {
        if (!isStageBody(d)) return
        const r = d.regions.find((x) => x.id === e.id)
        if (r) {
          if (e.index >= 0 && e.index < r.poly.outer.length) {
            r.poly.outer[e.index] = clampPt(e.to)
            r.provenance = humanProvenance()
          }
          return
        }
        const p = d.paths.find((x) => x.id === e.id)
        if (p && e.index >= 0 && e.index < p.points.length) {
          p.points[e.index] = clampPt(e.to)
          p.provenance = humanProvenance()
        }
      })
      return
    case 'insertVertex':
      state.patchDoc((d) => {
        if (!isStageBody(d)) return
        const r = d.regions.find((x) => x.id === e.id)
        if (r) {
          r.poly.outer.splice(e.index, 0, clampPt(e.at))
          r.provenance = humanProvenance()
          return
        }
        const p = d.paths.find((x) => x.id === e.id)
        if (p) {
          p.points.splice(e.index, 0, clampPt(e.at))
          p.provenance = humanProvenance()
        }
      })
      return
    case 'deleteVertex':
      state.patchDoc((d) => {
        if (!isStageBody(d)) return
        const r = d.regions.find((x) => x.id === e.id)
        if (r && r.poly.outer.length > 3) {
          r.poly.outer.splice(e.index, 1)
          r.provenance = humanProvenance()
          return
        }
        const p = d.paths.find((x) => x.id === e.id)
        if (p && p.points.length > 2) {
          p.points.splice(e.index, 1)
          p.provenance = humanProvenance()
        }
      })
      return
    case 'translateFeatures': {
      // A drag sends the total offset since it began: apply it to the pre-drag geometry, never on
      // top of the already-moved shape (that moved a +10 drag by +15).
      const base = e.fromStart && gestureStart ? gestureStart.doc : null
      if (base) state.patchDoc((d) => translateFrom(d, base, e.ids, e.delta))
      else state.patchDoc((d) => translateIn(d, e.ids, e.delta))
      return
    }
    case 'setHorizon':
      state.patchDoc((d) => {
        if (!isStageBody(d)) return
        if (d.depth.mode !== 'horizon') return
        d.depth.horizon_y = Math.min(100, Math.max(0, e.y))
      })
      return
    case 'createAnchor': {
      const id = newId('a')
      state.patchDoc((d) => {
        if (!isStageBody(d)) return
        d.anchors.push({
          id,
          kind: e.kind,
          at: clampPt(e.at),
          ...(e.stand ? { stand: clampPt(e.stand) } : {}),
          ...(e.facing ? { facing: e.facing as Facing } : {}),
          provenance: humanProvenance(),
        })
      })
      state.select([id])
      return
    }
    case 'moveAnchor':
      state.patchDoc((d) => {
        if (!isStageBody(d)) return
        const a = d.anchors.find((x) => x.id === e.id)
        if (!a) return
        a.at = clampPt(e.at)
        a.provenance = humanProvenance()
      })
      return
    case 'linkPortal':
      state.patchDoc((d) => {
        if (!isStageBody(d)) return
        const p = d.portals.find((x) => x.id === e.from_id)
        if (!p) return
        p.anchor_id = e.to_id
        delete p.edge
        p.provenance = humanProvenance()
      })
      return
    case 'createPortal': {
      if (!e.edge && !e.anchor_id) return
      const id = newId('p')
      const kind = e.kind ?? portalKindFor(e.anchor_id ? 'spot' : 'edge', state.activeKind.portal)
      state.patchDoc((d) => {
        if (!isStageBody(d)) return
        if (e.anchor_id && !d.anchors.some((a) => a.id === e.anchor_id)) return
        d.portals.push({
          id,
          kind,
          ...(e.anchor_id ? { anchor_id: e.anchor_id } : { edge: e.edge }),
          ...(e.transition ? { transition: e.transition } : {}),
          provenance: humanProvenance(),
        })
      })
      const after = useEditorStore.getState().doc
      if (isStageBody(after) && after.portals.some((p) => p.id === id)) state.select([id])
      return
    }
    case 'createPath': {
      const id = newId('w')
      const kind = e.kind ?? state.activeKind.path ?? 'walk'
      state.patchDoc((d) => {
        if (!isStageBody(d)) return
        d.paths.push({ id, kind, points: e.points.map(clampPt), provenance: humanProvenance() })
      })
      state.select([id])
      return
    }
    case 'setBaseline':
      state.patchDoc((d) => {
        if (!isStageBody(d)) return
        const y = Math.min(100, Math.max(0, e.y))
        const layer = d.layers.find((l) => l.id === e.id)
        if (layer) {
          layer.baseline_y = y
          layer.provenance = humanProvenance()
          return
        }
        const region = d.regions.find((r) => r.id === e.id)
        if (region) {
          region.baseline_y = y
          region.provenance = humanProvenance()
        }
      })
      return
    case 'panTo':
      state.setViewport({ tx: e.tx, ty: e.ty })
      return
    case 'zoomTo':
      state.setViewport({ k: Math.min(16, Math.max(0.1, e.scale)) })
      return
    case 'toast':
      toastSink?.({ message: e.message, tone: e.tone })
      return
  }
}

/* -------------------------------------------------------------------------- */
/* Selectors + undo helpers                                                    */
/* -------------------------------------------------------------------------- */

export const selectCanUndo = (s: EditorState): boolean => s.history.past.length > 0 && !s.gesturing
export const selectCanRedo = (s: EditorState): boolean => s.history.future.length > 0 && !s.gesturing

/** Re-renders when undo becomes (un)available. */
export function useCanUndo(): boolean {
  return useEditorStore(selectCanUndo)
}

export function useCanRedo(): boolean {
  return useEditorStore(selectCanRedo)
}

export function undo(): void {
  useEditorStore.getState().undo()
}

export function redo(): void {
  useEditorStore.getState().redo()
}

export function canUndo(): boolean {
  return selectCanUndo(useEditorStore.getState())
}

export function canRedo(): boolean {
  return selectCanRedo(useEditorStore.getState())
}

/** The document the server holds, as far as this editor knows (for tests and the leave guard). */
export function lastSavedDocument(): EditorDoc | null {
  return lastSavedDoc
}

/** True when `doc`'s lints came from the server for exactly this document (no dry-run needed). */
export function hasServerLints(doc: EditorDoc | null): boolean {
  return !!doc && serverLinted.has(doc)
}

/** Is this feature owned by a human (so a re-run must not replace it)? */
export function isLocked(doc: EditorDoc | null, id: string): boolean {
  if (!isStageBody(doc)) return false
  return stageFeatures(doc).find((f) => f.id === id)?.provenance.locked === true
}

export { rectRing }
